import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Users, Filter, RefreshCw, Search, Clock } from 'lucide-react';
import { useAcmBase } from '../hooks/useAcmBase';
import { ExportButton } from '../components/ExportButton';
import { useSidebarState } from '../hooks/useSidebarState';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable } from "@/components/ui/data-table";
import { StudentProfileModal } from '@/components/StudentProfileModal';
import { StudentMetricsCards } from '@/components/StudentMetricsCards';

export function StudentsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState('all');
  const [filters, setFilters] = useState({
    status: '',
    curso: 'todos',
    dataInicio: '',
    dataFim: '',
    acessoFiltro: '',
    ordenacao: 'recentes'
  });
  const [selectedStudent, setSelectedStudent] = useState(null);

  const { isCollapsed } = useSidebarState();

  const { 
    data: students,
    loading,
    refresh,
    availableCourses,
    totalPages,
    currentPage: fetchedPage
  } = useAcmBase({
    page: currentPage,
    searchTerm: debouncedSearch,
    filters: {
      ...filters,
      studentType: activeFilter
    }
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleRefresh = async () => {
    try {
      await refresh();
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
    }
  };

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const columns = [
    {
      accessorKey: "nome",
      header: "Nome",
    },
    {
      accessorKey: "email",
      header: "E-mail",
    },
    {
      accessorKey: "documento",
      header: "Documento",
    },
    {
      accessorKey: "telefone",
      header: "Telefone",
    },
    {
      accessorKey: "cursos",
      header: "Cursos",
      cell: ({ row }) => {
        const cursos = row.original.cursos;
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm">{cursos.length}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "ultimo_acesso",
      header: "Último Acesso",
      cell: ({ row }) => {
        const date = row.original.ultimo_acesso;
        if (!date) return "Nunca acessou";
        return date;
      }
    }
  ];

  return (
    <div className="min-h-screen bg-background text-white">
      {/* Header */}
      <div className="bg-background-lighter border-b border-border">
        <div className="p-8">
          <div className="flex justify-between items-start mb-8">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline"
                size="icon"
                onClick={() => window.history.back()}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6 text-primary" />
                  <h1 className="text-2xl font-bold">Lista de Alunos</h1>
                </div>
                <p className="text-muted-foreground text-sm mt-1">
                  Gerencie e visualize todos os alunos cadastrados
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefresh}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="outline">
                <Filter className="w-4 h-4 mr-2" />
                Filtros avançados
              </Button>
              <ExportButton 
                students={students} 
                isLoading={loading}
                searchTerm={searchTerm}
                filters={filters}
              />
            </div>
          </div>

          {/* Cards de Métricas */}
          <StudentMetricsCards 
            onFilterChange={handleFilterChange}
            activeFilter={activeFilter}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Buscar por nome, e-mail ou documento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={filters.curso}
                onValueChange={(value) => setFilters(prev => ({ ...prev, curso: value }))}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filtrar por curso" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os cursos</SelectItem>
                  {availableCourses?.map((curso) => (
                    <SelectItem key={curso} value={curso}>
                      {curso}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.ordenacao}
                onValueChange={(value) => setFilters(prev => ({ ...prev, ordenacao: value }))}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recentes">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Mais recentes
                    </div>
                  </SelectItem>
                  <SelectItem value="antigos">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Mais antigos
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable 
              columns={columns} 
              data={students}
              loading={loading}
              onRowClick={setSelectedStudent}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </CardContent>
        </Card>
      </div>

      {/* Modal da ficha do aluno */}
      <StudentProfileModal
        student={selectedStudent}
        isOpen={!!selectedStudent}
        onClose={() => setSelectedStudent(null)}
      />
    </div>
  );
}